import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SplashScreen } from '@capacitor/splash-screen';
import { Button } from 'primereact/button';
import { NoAuthPageTemplate } from '../../layout/NoAuthPageTemplate/NoAuthPageTemplate';
import {
  applyLatestOtaBundle,
  clearOtaReturnPath,
  loadOtaWebReturnPath,
  OtaForcedUpdateResult,
  readOtaReturnPath,
  saveOtaReturnPath,
} from '../../utils/otaUtils';

type Status = 'updating' | Exclude<OtaForcedUpdateResult, 'applying'>;

/** While no bundle is available, check again this often: it may be published a little later. */
const NO_UPDATE_RECHECK_MS = 30 * 1000;

const goToApp = () => window.location.assign('/');

/**
 * Applies the latest OTA bundle (the app reloads). Opened when the backend answers `OTA_BUNDLE_VERSION_TOO_LOW` (forced:
 * the user cannot go on without it), or with `?optional=1&from=<path>` when the app returns from the background and a
 * new bundle exists (optional: if it cannot be applied, the user goes back to `from`).
 * On the web there is no bundle to apply (`applyLatestOtaBundle` answers `'web'`): the page says the update is not
 * available yet and, every 30 s and on "Reintentar", loads the `from` path of the page (a full page load) to try again.
 * Separate from `/update-app`, which sends store builds that are too old to the store.
 */
export const OtaUpdate = () => {
  const [status, setStatus] = useState<Status>('updating');

  const optionalReturnPath = useMemo(() => readOtaReturnPath(window.location.search), []);

  const update = useCallback(
    async (ignoreRecentAttempt: boolean) => {
      setStatus('updating');
      // The plugin reloads the app at `/` after the update; the saved path brings the user back to their page.
      if (optionalReturnPath) saveOtaReturnPath(optionalReturnPath);
      const result = await applyLatestOtaBundle({ ignoreRecentAttempt });
      if (result === 'applying') return;
      if (optionalReturnPath) {
        clearOtaReturnPath();
        window.location.assign(optionalReturnPath);
        return;
      }
      setStatus(result);
    },
    [optionalReturnPath],
  );

  useEffect(() => {
    SplashScreen.hide();
    update(false);
  }, [update]);

  useEffect(() => {
    if (status !== 'web') return;
    const id = setInterval(() => loadOtaWebReturnPath(window.location.search), NO_UPDATE_RECHECK_MS);
    return () => clearInterval(id);
  }, [status]);

  useEffect(() => {
    if (status !== 'no-update') return;
    const interval = setInterval(async () => {
      if ((await applyLatestOtaBundle({ ignoreRecentAttempt: true })) === 'applying') clearInterval(interval);
    }, NO_UPDATE_RECHECK_MS);
    return () => clearInterval(interval);
  }, [status]);

  if (status === 'updating') {
    return (
      <NoAuthPageTemplate icon='pi pi-spin pi-spinner'>
        <h2>Actualizando la aplicación</h2>
        <p>Estamos instalando la versión más reciente. Tardará unos segundos.</p>
      </NoAuthPageTemplate>
    );
  }

  if (status === 'recently-applied') {
    return (
      <NoAuthPageTemplate icon='pi pi-clock' color='info'>
        <h2>La actualización todavía no está lista</h2>
        <p>La aplicación ya tiene la última versión publicada. Inténtalo de nuevo en unos minutos.</p>
        <Button label='Reintentar' onClick={goToApp} />
      </NoAuthPageTemplate>
    );
  }

  if (status === 'no-update' || status === 'web') {
    return (
      <NoAuthPageTemplate icon='pi pi-clock' color='info'>
        <h2>La actualización todavía no está disponible</h2>
        <p>Lo comprobaremos de nuevo automáticamente en unos segundos.</p>
        <Button label='Reintentar' onClick={status === 'web' ? () => loadOtaWebReturnPath(window.location.search) : goToApp} />
      </NoAuthPageTemplate>
    );
  }

  return (
    <NoAuthPageTemplate icon='pi pi-exclamation-triangle' color='error'>
      <h2>No se pudo actualizar la aplicación</h2>
      <p>Comprueba tu conexión e inténtalo de nuevo.</p>
      <Button label='Reintentar' onClick={() => update(true)} />
    </NoAuthPageTemplate>
  );
};
